import { Injectable } from '@angular/core';
import { ApplicationConfig, Module, ProjectInfo } from '@frontend-site/shared/data';
import { AngularModeService, ProjectStore, SessionRepository } from '@frontend-site/shared/core';
import { combineLatest, filter, map, Observable } from 'rxjs';
import { DefaultMainHeaderMenu, MainHeaderMenu, MenuStates } from '../../models';
import { MenuBuilderService } from './menu-builder';
import { createEmptyMenu } from './empty-menu-builder';
import { createOverviewMenu } from './in-project/overview-menu/overview-menu-factory';
import { DevelopmentMenuFactory } from './in-project/development-menu-factory';
import { TestsMenuFactory } from './in-project/tests-menu-factory';
import { createDefectsMenu } from './in-project/defects-menu-factory';
import { CorrectionsMenuFactory } from './in-project/corrections-menu-factory';
import { createReportsMenu } from './in-project/reports-menu-factory';
import { createDiscoveryMenu } from './in-project/discovery-menu-factory';

@Injectable()
export class InProjectMenuBuilderService implements MenuBuilderService {
    get menuState(): MenuStates {
        return MenuStates.PROJECT;
    }

    constructor(
        private readonly projectStore: ProjectStore,
        private readonly sessionStore: SessionRepository,
        private readonly developmentMenuFactory: DevelopmentMenuFactory,
        private readonly testsMenuFactory: TestsMenuFactory,
        private readonly angularModeService: AngularModeService,
        private readonly correctionsMenuFactory: CorrectionsMenuFactory
    ) {}

    build$(): Observable<MainHeaderMenu | null> {
        return combineLatest([
            this.projectStore.projectDetails$,
            this.sessionStore.applicationConfig$,
            this.sessionStore.legacyAppVersion$
        ]).pipe(
            filter(([projectInfo, appConfig]) => !!projectInfo && !!appConfig),
            map(([projectInfo, appConfig, legacyAppVersion]) =>
                this.createMenu(projectInfo as ProjectInfo, appConfig as ApplicationConfig, legacyAppVersion)
            )
        );
    }

    createMenu(projectInfo: ProjectInfo, appConfig: ApplicationConfig, legacyAppVersion: string | undefined): MainHeaderMenu | null {
        if (this.shouldNotShowMenu(projectInfo)) {
            return createEmptyMenu(this.menuState, projectInfo.projectName);
        }

        if (projectInfo.qc) {
            const testsMenu = this.testsMenuFactory.create(projectInfo);
            return new DefaultMainHeaderMenu(this.menuState, { title: projectInfo.projectName, items: testsMenu ? [testsMenu] : [] });
        }

        const menu = new DefaultMainHeaderMenu(this.menuState, { title: projectInfo.projectName, showSearch: true });

        menu.addMenuItem(
            createOverviewMenu(
                projectInfo,
                appConfig,
                legacyAppVersion,
                this.angularModeService.isNewUi(Module.ForesightAllComponents),
                this.angularModeService.isNewUi(Module.UnifiedProjectPlan)
            )
        )
            .addMenuItem(this.createDevelopmentMenu(projectInfo, appConfig))
            .addMenuItem(this.testsMenuFactory.create(projectInfo))
            .addMenuItem(createDefectsMenu(projectInfo))
            .addMenuItem(this.correctionsMenuFactory.createCorrectionsMenu(projectInfo))
            .addMenuItem(createReportsMenu(projectInfo))
            .addMenuItem(createDiscoveryMenu(projectInfo, this.angularModeService.isNewUi(Module.Foresight)));

        return menu;
    }

    private createDevelopmentMenu(projectInfo: ProjectInfo, appConfig: ApplicationConfig) {
        if (projectInfo.testAutomationStandalone) {
            return null;
        }
        return this.developmentMenuFactory.create(projectInfo, appConfig.companyId);
    }

    private shouldNotShowMenu(projectInfo: ProjectInfo): boolean {
        return projectInfo.accountOnTrial && !projectInfo.panayaUser && !projectInfo.isS4Project() && !projectInfo.isCleanCoreProject();
    }
}
